import { makeAutoObservable } from 'mobx';
import apiConfig from '../api/routes';
import {
  EmployeeEvent,
  EmployeesEvents,
  EmployeesPlanerI,
  ImageType,
  PlannerFilterDto,
  PlannerListDTO,
  PlannerListMap
} from '../types';
import { groupBy } from 'fp-ts/NonEmptyArray';
import { dateToYMDFormat } from '../utils/dateHelper';
import { notification, NotificationType } from '@iba-team/portal-react-components/dist';

class Planner {
  constructor() {
    makeAutoObservable(this);
  }

  listEmployees: Array<EmployeesPlanerI> = [];
  page = 1;
  limit = 10;
  employeesEvents: EmployeesEvents[] = [];
  images: ImageType[] = [];
  fetchingEvents = false;
  plannerLists: PlannerListMap = { DIVISION: [], PROJECT: [], CUSTOM: [], CIRCLE: [] };
  selectedRange: EmployeeEvent[] = [];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  selectListFilter: PlannerFilterDto = [];
  setSelectedRange = (update: EmployeeEvent[]) => {
    this.selectedRange = update;
  };


  getPlannerFilters = async () => {
    this.selectListFilter = await apiConfig.settingsService.getPlannerFilters();
  };

  addPlannerFilter = async (filters: PlannerFilterDto, collectStatistics?: boolean) => {
    await apiConfig.settingsService.addPlannerFilters(filters, collectStatistics);
  };

  getEmployeesEvents = async (year: number, employeeIds: number[], plannerListType: string, projectName: string) => {
    this.fetchingEvents = true;
    try {
      const events = await apiConfig.plannerService.getEmployeesEventsList(year, employeeIds, plannerListType, projectName);
      const prepared = events.map(user => {
        const userEvents = Object.values(groupBy<EmployeeEvent>(i => dateToYMDFormat(i.startDate))(user.events));
        const convert = userEvents.map(evtArr => {
          const findPlanned = evtArr.find(i => i.type === 'PLANNED');
          const findOther = evtArr.find(i => (i.type && i.type !== 'PLANNED'));
          if(findPlanned && findOther?.type !== 'PLANNED'){
            if(findOther?.originalTypeName){
              return {
                default: findPlanned,
                new: findOther!,
              };
            }
            else{
              return {
                default: findOther!,
                new: findPlanned
              };
            }
          }
          else{
            return {
              default: findOther!,
            };
          }
        });
        return {...user, events: convert};
      });
      this.employeesEvents = prepared;
      setTimeout(() => {
        this.fetchingEvents = false;
      },500);
    }
    catch (e) {
      this.fetchingEvents = false;
      throw new Error('Error! Couldn\'t get employees events!');
    }
  };
  deletePlanningAbsences = async (dateSet: string[]) => {
    try{
      await apiConfig.plannerService.deletePlanningAbsences(dateSet);
    }
    catch (e) {
      notification.open({
        title: 'Error',
        type: NotificationType.ERROR,
        description: 'Something went wrong while deleting planned absences'
      });
    }
  };
  createPlannedAbsences = async (dateSet: string[]) => {
    try{
      await apiConfig.plannerService.createPlanningAbsences(dateSet);
    }
    catch (e) {
      notification.open({
        title: 'Error',
        type: NotificationType.ERROR,
        description: 'Something went wrong while creating planned absences'
      });
    }
  };

  clearEmployeesEvents = () =>{
    this.employeesEvents =[];
  };

  getListEmployees = async (value:string,ignoreEmployees:Array<number>) =>{
    const response = await apiConfig.plannerService.getEmployees(ignoreEmployees,value, this.page, this.limit);
    this.listEmployees = response.employees;
  };
  clearListEmployees = () =>{
    this.listEmployees = [];
  };
  updatePlannerList = async (id: number, userId: number) => {
    return apiConfig.plannerService.updatePlannerList(id, userId);
  };

  createEmployeeList = async (data: Omit<PlannerListDTO, 'id'| 'plannerListType'>, isForWhoisOffToday?: boolean) => {
    return await apiConfig.plannerService.createEmployeeList(data, isForWhoisOffToday);
  };

  fetchPlannerLists = async (collectStatistics?: boolean, isForWhoisOffToday?: boolean) => {
    this.plannerLists =  await apiConfig.plannerService.getPlannerLists(collectStatistics, isForWhoisOffToday);
  };

  deletePlannerList = async (id:number) => {
    this.clearEmployeesEvents();
    await apiConfig.plannerService.deletePlanerList(id);
  };

  removeEmployee = async (listId: number, userIds: number[]) => {
    await apiConfig.plannerService.removeEmployee(listId, userIds);
    this.employeesEvents = this.employeesEvents.filter(i => i.id !== userIds[0]);
  };

}

export const PlannerStore = new Planner();
